import { Elements } from "@stripe/react-stripe-js"
import PropTypes from "prop-types"
import React, { createContext, useContext, useEffect, useState } from "react"

import AlertBanner from "src/components/AlertBanner"

import { STRIPE_LOAD_ERROR_MESSAGE, getStripe } from "src/utils/stripe"

const RetryStripeLoadContext = createContext(null)

export const StripeLoadError = () => {
  const retry = useContext(RetryStripeLoadContext)

  if (!retry) return null

  return (
    <AlertBanner
      type="error"
      cta={{ type: "secondary", text: "Try again", onClick: retry }}
    >
      {STRIPE_LOAD_ERROR_MESSAGE}
    </AlertBanner>
  )
}

// Mount this with the payment form, not above it: each mount requests
// Stripe.js again, so a form that opens after a failed load retries it.
// Elements starts with a null stripe and takes the instance once it loads, so
// a retry from StripeLoadError keeps the form and its state mounted.
// A form that renders its fields in a modal passes inlineLoadError={false}
// and renders StripeLoadError inside the modal.
const StripeElements = ({ options, children, inlineLoadError = true }) => {
  const [stripe, setStripe] = useState(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let mounted = true

    getStripe().then(
      (loaded) => mounted && setStripe(loaded),
      () => mounted && setFailed(true)
    )

    return () => {
      mounted = false
    }
  }, [attempt])

  const retry = () => {
    setFailed(false)
    setAttempt((count) => count + 1)
  }

  return (
    <RetryStripeLoadContext.Provider value={failed ? retry : null}>
      <Elements stripe={stripe} options={options}>
        {inlineLoadError && <StripeLoadError />}
        {children}
      </Elements>
    </RetryStripeLoadContext.Provider>
  )
}

StripeElements.propTypes = {
  options: PropTypes.object,
  children: PropTypes.node.isRequired,
  inlineLoadError: PropTypes.bool,
}

export default StripeElements
